import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, CheckCircle, ArrowRight, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { takeReturnPath } from '../lib/returnPath';
import './AuthPages.css';

/**
 * /confirm-email/:token — B-65. The "Confirm my email" button in our email
 * lands here.
 *
 * Coordinator, 15 Oct (closes the squatter gap): the link confirms only for
 * the account it was sent for, SIGNED IN on this device. Signed out → "Sign in
 * to confirm your email", to the login page with a return path back here
 * (state.from, as UC-B5), and it confirms after sign-in. The link stays
 * usable until it's used or expires. Signed in as someone else → refused,
 * with a Sign out button. Confirming never creates a session.
 */
const ConfirmEmailPage = () => {
  const { token } = useParams();
  const { user, loading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // checking | signin | confirmed | already | wrong | error
  const [state, setState] = useState('checking');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sentFor = useRef(null); // once per signed-in account, even under StrictMode

  const toLogin = () => navigate('/login', { state: { from: location.pathname } });

  useEffect(() => {
    if (loading) return;
    if (!user) { setState('signin'); return; }
    if (sentFor.current === user.id) return;
    sentFor.current = user.id;
    setState('checking');
    axios.post('/auth/confirm-email', { token })
      .then(res => setState(res.data?.alreadyConfirmed ? 'already' : 'confirmed'))
      .catch(err => {
        setError(err?.response?.data?.error || 'We couldn\'t confirm your email just now. Please try the link again.');
        setState(err?.response?.data?.code === 'wrong_account' ? 'wrong' : 'error');
      });
  }, [token, user, loading]);

  const goOn = async () => {
    setBusy(true);
    try {
      if (user) {
        await refreshUser(); // the Check your inbox screen lets them in now
        // WT-5: a new account that signed up from a club page ("Join the
        // club") goes back there to ask to join; Register kept the path on
        // this device for this account. Only once confirmed: a failed link
        // keeps it for the next one.
        const confirmed = state === 'confirmed' || state === 'already';
        navigate((confirmed && takeReturnPath(user.id)) || '/my-clubs', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } finally { setBusy(false); }
  };

  const switchAccount = () => { logout(); toLogin(); };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo-wrap">
          <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
        </div>
        {state === 'checking' && (
          <>
            <h1>Confirming your email</h1>
            <p className="auth-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="spinner" /> One moment…
            </p>
          </>
        )}
        {state === 'signin' && (
          <>
            <h1>Sign in to confirm your email</h1>
            <p className="auth-subtitle">
              For your security, sign in to the Marakin account this email was sent for. We'll confirm it straight after.
            </p>
            <button type="button" className="btn btn-primary auth-submit" onClick={toLogin}>
              <LogIn size={16} /> Sign in
            </button>
          </>
        )}
        {(state === 'confirmed' || state === 'already') && (
          <>
            <div className="confirm-gate-icon confirm-gate-icon-ok"><CheckCircle size={26} /></div>
            <h1>Email confirmed</h1>
            <p className="auth-subtitle">
              {state === 'already'
                ? 'Your email was already confirmed. You\'re all set.'
                : 'Thanks, your email is confirmed. You\'re all set.'}
            </p>
            <button type="button" className="btn btn-primary auth-submit" onClick={goOn} disabled={busy}>
              Continue to Marakin <ArrowRight size={16} />
            </button>
          </>
        )}
        {state === 'wrong' && (
          <>
            <h1>Confirm your email</h1>
            <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>
            <button type="button" className="btn btn-primary auth-submit" onClick={switchAccount}>
              <LogIn size={16} /> Sign out and switch account
            </button>
          </>
        )}
        {state === 'error' && (
          <>
            <h1>Confirm your email</h1>
            <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>
            <button type="button" className="btn btn-primary auth-submit" onClick={goOn} disabled={busy}>
              Back to Marakin <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmailPage;
